import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = { name: "", email: "", message: "" };

export const ConnectModal = ({ open, onClose }) => {
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const firstInputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setSent(false);
            setForm(initialForm);
            // focus after animation
            setTimeout(() => firstInputRef.current?.focus(), 220);
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape" && open) onClose?.();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        const { name, email, message } = form;
        if (!name.trim() || !email.trim() || !message.trim()) {
            toast("A few words are missing.", {
                description: "Please share your name, email, and a note.",
            });
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, {
                name: name.trim(),
                email: email.trim(),
                message: message.trim(),
            });
            setSent(true);
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                "Something interrupted the moment. Please try again.";
            toast("Message not sent", { description: detail });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    data-testid="connect-modal-backdrop"
                    className="fixed inset-0 z-50 flex items-center justify-center px-5"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                    {/* Backdrop */}
                    <button
                        aria-label="Close"
                        onClick={onClose}
                        className="absolute inset-0"
                        style={{
                            background: "rgba(247,243,236,0.85)",
                            backdropFilter: "blur(8px)",
                            WebkitBackdropFilter: "blur(8px)",
                        }}
                        data-testid="connect-modal-backdrop-button"
                    />

                    {/* Panel */}
                    <motion.div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="connect-title"
                        className="relative w-full max-w-[560px]"
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{
                            duration: 0.7,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                        data-testid="connect-modal"
                    >
                        <div
                            className="relative px-8 sm:px-12 py-12 sm:py-14"
                            style={{
                                background: "var(--paper)",
                                border: "1px solid var(--hairline)",
                            }}
                        >
                            <button
                                onClick={onClose}
                                aria-label="Close"
                                data-testid="connect-modal-close"
                                className="absolute top-5 right-5 p-1 transition-opacity hover:opacity-60"
                                style={{ color: "var(--sage-text)" }}
                            >
                                <X size={16} strokeWidth={1} />
                            </button>

                            {!sent ? (
                                <>
                                    <form
                                        onSubmit={handleSubmit}
                                        className="space-y-9"
                                        data-testid="connect-form"
                                    >
                                        <FieldLine
                                            label="Your name"
                                            name="name"
                                            value={form.name}
                                            onChange={handleChange}
                                            ref={firstInputRef}
                                            testId="connect-input-name"
                                        />
                                        <FieldLine
                                            label="Your email"
                                            name="email"
                                            type="email"
                                            value={form.email}
                                            onChange={handleChange}
                                            testId="connect-input-email"
                                        />
                                        <FieldArea
                                            label="A note"
                                            name="message"
                                            value={form.message}
                                            onChange={handleChange}
                                            testId="connect-input-message"
                                        />

                                        <div className="pt-4 flex items-center justify-between">
                                            <button
                                                type="button"
                                                onClick={onClose}
                                                className="serif italic text-sm tracking-wide transition-opacity hover:opacity-60"
                                                style={{
                                                    color: "var(--ink-soft)",
                                                }}
                                                data-testid="connect-cancel"
                                            >
                                                cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={submitting}
                                                className="serif text-xs tracking-[0.36em] uppercase underline-grow"
                                                style={{
                                                    color: "var(--sage-text)",
                                                }}
                                                data-testid="connect-submit"
                                            >
                                                {submitting
                                                    ? "sending"
                                                    : "send"}
                                            </button>
                                        </div>
                                    </form>
                                </>
                            ) : (
                                <motion.div
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8 }}
                                    className="py-10 text-center"
                                    data-testid="connect-success"
                                >
                                    <p
                                        className="eyebrow mb-6"
                                        style={{ color: "var(--sage-text)" }}
                                    >
                                        received
                                    </p>
                                    <h3
                                        className="serif font-light text-xl sm:text-2xl mb-6"
                                        style={{ color: "var(--sage-text)" }}
                                    >
                                        Thank you.
                                    </h3>
                                    <p
                                        className="serif text-base"
                                        style={{ color: "var(--sage-text)", opacity: 0.85 }}
                                    >
                                        Your words have arrived. A reply will
                                        find you soon.
                                    </p>
                                    <button
                                        onClick={onClose}
                                        className="serif mt-12 text-xs tracking-[0.3em] uppercase transition-opacity hover:opacity-60"
                                        style={{ color: "var(--sage-text)" }}
                                        data-testid="connect-success-close"
                                    >
                                        close
                                    </button>
                                </motion.div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const FieldLine = React.forwardRef(function FieldLine(
    { label, name, type = "text", value, onChange, testId },
    ref,
) {
    return (
        <label className="block">
            <span
                className="eyebrow block mb-2"
                style={{ color: "var(--sage-text)" }}
            >
                {label}
            </span>
            <input
                ref={ref}
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                autoComplete="off"
                className="w-full bg-transparent serif text-base pb-2 outline-none transition-colors"
                style={{
                    color: "var(--sage-text)",
                    borderBottom: "1px solid var(--hairline)",
                }}
                onFocus={(e) =>
                    (e.currentTarget.style.borderBottomColor = "var(--sage-text)")
                }
                onBlur={(e) =>
                    (e.currentTarget.style.borderBottomColor =
                        "var(--hairline)")
                }
                data-testid={testId}
            />
        </label>
    );
});

const FieldArea = ({ label, name, value, onChange, testId }) => (
    <label className="block">
        <span
            className="eyebrow block mb-2"
            style={{ color: "var(--sage-text)" }}
        >
            {label}
        </span>
        <textarea
            name={name}
            value={value}
            onChange={onChange}
            rows={3}
            className="w-full bg-transparent serif text-base pb-2 outline-none resize-none transition-colors"
            style={{
                color: "var(--sage-text)",
                borderBottom: "1px solid var(--hairline)",
            }}
            onFocus={(e) =>
                (e.currentTarget.style.borderBottomColor = "var(--sage-text)")
            }
            onBlur={(e) =>
                (e.currentTarget.style.borderBottomColor = "var(--hairline)")
            }
            data-testid={testId}
        />
    </label>
);

export default ConnectModal;
